import React from 'react';
import { Box, ListItem, Typography, Avatar } from '@mui/material';
import { SportEvent } from '../types/SportEventTypes';
import { EventActions } from './EventActions';
import {
  goalEventRowStyle,
  scoreDisplayStyle,
  timeTextStyle,
  descriptionTextStyle,
  scoreTextStyle,
  teamLogoStyle,
  scoreBoardLogoStyle,
  scoreBoardAvatarStyle,
  newEventFlashStyle,
  playheadOpacityStyle,
} from '../styles/playByPlayStyles';

interface GoalEventItemProps {
  event: SportEvent;
  teamColor: string;
  textColor: string;
  teamLogoPath: string;
  homeTeamLogoPath: string;
  awayTeamLogoPath: string;
  homeTeamPrimaryColor: string;
  awayTeamPrimaryColor: string;
  gameTimePrefix?: string;
  gameTime: string;
  description: string;
  score: { home: number; away: number } | null;
  onDebug: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onClipCreate: (event: SportEvent) => void;
  clipGroupCount?: number;
  isShiftEvent?: boolean;
  /** Row just arrived live — plays a one-off highlight that fades out. */
  isNew?: boolean;
  /** Event is past the stream's content end — rendered dimmed and inert. */
  isAhead?: boolean;
}

export const GoalEventItem: React.FC<GoalEventItemProps> = ({
  event,
  teamColor,
  textColor,
  teamLogoPath,
  homeTeamLogoPath,
  awayTeamLogoPath,
  homeTeamPrimaryColor,
  awayTeamPrimaryColor,
  gameTimePrefix,
  gameTime,
  description,
  score,
  onDebug,
  onSeekToEvent,
  onClipCreate,
  isShiftEvent,
  isNew,
  isAhead,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: teamColor,
        '&:hover': {
          cursor: `pointer`,
        },
        ...(isNew && newEventFlashStyle),
        ...(isAhead !== undefined && playheadOpacityStyle(isAhead)),
      }}
      onClick={() => onSeekToEvent(event)}
    >
      <ListItem sx={goalEventRowStyle(teamColor)}>
        {/* Time */}
        <Typography sx={{ ...timeTextStyle, color: textColor, '&:hover': { cursor: 'pointer' } }}>
          {gameTimePrefix && (
            <Box
              component="span"
              sx={(theme) => ({  mr: 0.5 })}
            >
              {gameTimePrefix}
            </Box>
          )}
          {gameTime}
        </Typography>

        {/* Team Logo */}
        {teamLogoPath ? (
          <Box component="img" src={teamLogoPath} alt="team" sx={teamLogoStyle} />
        ) : (
          <Avatar sx={{ width: 32, height: 32, bgcolor: teamColor }}>🏒</Avatar>
        )}

        {/* Description */}
        <Typography sx={{ ...descriptionTextStyle, fontWeight: 500, color: textColor }}>
          {description}
        </Typography>

        {/* Actions */}
        <EventActions
          event={event}
          onDebug={onDebug}
          textColor={textColor === '#000000' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'}
          onClipCreate={onClipCreate}
          disabled={isAhead}
        />
      </ListItem>
      {score && (
        <Box sx={{ ...scoreDisplayStyle, ...(isShiftEvent && { borderBottom: 'none' }) }}>
          {/* Away Team Logo */}
          {awayTeamLogoPath ? (
            <Box component="img" src={awayTeamLogoPath} alt="Away team" sx={scoreBoardLogoStyle} />
          ) : (
            <Avatar sx={scoreBoardAvatarStyle(awayTeamPrimaryColor)}>🏒</Avatar>
          )}

          {/* Score */}
          <Typography sx={{ ...scoreTextStyle, color: textColor }}>
            {score.away} - {score.home}
          </Typography>
          {/* Home Team Logo */}
          {homeTeamLogoPath ? (
            <Box component="img" src={homeTeamLogoPath} alt="Home team" sx={scoreBoardLogoStyle} />
          ) : (
            <Avatar sx={scoreBoardAvatarStyle(homeTeamPrimaryColor)}>🏒</Avatar>
          )}
        </Box>
      )}
    </Box>
  );
};
